/*!
// <copyright file="MessageCenter.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { util, $format } from "@ax/office-shared-lib";
import { resources } from "@ax/office-cdn-app-resources";
import { appElements } from "./Elements";
import { notifications } from "./Notifications";
import { dialogs } from "./Dialogs";
import { operationHandling } from "./OperationHandling";
import * as Globalize from "globalize";
import * as $ from "jquery";
import "jquery-ui/ui/keycode";

export namespace messageCenter {

    interface MessageCenterselectShownNotificationElementOptions {

        /** The index that should be selected; index 0 by default. A value of -1 indicates that the last item will be selected. */
        indexToSelect?: number;

        /** Indicates whether the selected element should receive focus; true by default. */
        setFocus?: boolean;
    }

    /** Handles the message center for the app. */
    export class MessageCenterHandler {

        /** Indicates whether errors should be the only messages shown. */
        private _showErrorsOnly: boolean;

        /** The message center dialog element. */
        private _messageCenterDialog: JQuery;

        /** The messages container element. */
        private _messagesContainer: JQuery;

        /** The help and support link element. */
        private _messageLearnMore: JQuery;

        /** The checkbox to toggles whether errors only should be displayed. */
        private _errorsOnlyCheckbox: JQuery;

        /** The message center's clear button. */
        private _clearButton: JQuery;

        /** The message center's done button. */
        private _doneButton: JQuery;

        /** The formatted string used to create the HTML for a notification item. */
        private static _notificationItemHtmlFormat =
        '<li>' +
        '<span>{0} {1}</span>' +
        '<p class="dynamicsSymbolItem" data-has-link="{2}"></p>' +
        '</li>';

        /** The formatted string used to create the HTML for a notification item without a time stamp. */
        private static _notificationItemNoTimeHtmlFormat =
        '<li>' +
        '<p class="dynamicsSymbolItem" data-has-link="{0}"></p>' +
        '</li>';

        /** The IDs of controls relating to the message center required by the message center handler. */
        private static ControlIDs = {

            /** ID of the message center dialog. */
            messageCenterDialog: "messageCenter",

            /** ID of the messages container. */
            messagesContainer: "messagesContainer",

            /** ID of the no messages text heading. */
            noMessagesText: "noMessagesText",

            /** ID of the help and support link element. */
            learnMoreText: "messageLearnMore",

            /** ID of the errors only checkbox. */
            errorsOnlyCheckbox: "showErrorsOnlyInput",

            /** ID of the errors only label. */
            errorsOnlyLabel: "showErrorsOnlyLabel",

            /** ID of the clear button. */
            clearButton: "messageCenterClearButton",

            /** ID of the done button. */
            doneButton: "messageCenterDoneButton"
        };

        /** The attributes to apply to the message center. */
        private static Attributes = {

            /** The HTML ID attribute. */
            id: "id",

            /** The HTML tab index attribute. */
            tabIndex: "tabindex",

            /** The ARIA role attribute. */
            role: "role",

            /** The has messages attribute, indicating if the message center currently contains any messages. */
            hasMessages: "data-has-messages",

            /** Boolean attribute to show the help and support link */
            showLearnMoreLink: "learn-more-link",

            /** The errors only attribute, indicates if errors are the only messages that should be shown. */
            errorsOnly: "data-errors-only",

            /** The attribute applied to notification elements that indicate what type of notification they represent. */
            notificationType: "data-notification-type"
        };

        /** The selectors used to find elements within the message center. */
        private static Selectors = {

            /** Used to find notification items. */
            notificationItem: `[${MessageCenterHandler.Attributes.notificationType}]`,

            /** Used to find only error notification items. */
            errorNotificationItem: `[${MessageCenterHandler.Attributes.notificationType}="${notifications.NotificationType.error}"]`,

            /** Used to find only the selected notification item. */
            selectedNotificationItem: `[${MessageCenterHandler.Attributes.notificationType}][${util.AriaAttributes.Selected}="true"]`
        };

        /* jshint -W040 */
        constructor() {
            this._showErrorsOnly = false;
            this.clearNotifications = this.clearNotifications.bind(this);
            this.openDialog = this.openDialog.bind(this);
            this.closeDialog = this.closeDialog.bind(this);
            this.showErrorsOnly = this.showErrorsOnly.bind(this);
            this._updateShowErrorsOnly = this._updateShowErrorsOnly.bind(this);
            this._messagesContainerKeyDown = this._messagesContainerKeyDown.bind(this);
            this._messagesContainerClicked = this._messagesContainerClicked.bind(this);
        }
        /* jshint +W040 */

        /** Initializes the message center handler. */
        public initialize(): void {
            this._messageCenterDialog = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.messageCenterDialog, appElements.contentDiv);
            this._messagesContainer = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.messagesContainer, this._messageCenterDialog);
            this._messageLearnMore = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.learnMoreText, this._messageCenterDialog);
            this._errorsOnlyCheckbox = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.errorsOnlyCheckbox, this._messageCenterDialog);
            this._clearButton = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.clearButton, this._messageCenterDialog);
            this._doneButton = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.doneButton, this._messageCenterDialog);

            this._messagesContainer.attr(util.AriaAttributes.Label, resources.dynamicsApp.messageCenterMessageContainerLabel());
            this._messagesContainer.keydown(this._messagesContainerKeyDown);
            this._messagesContainer.click(this._messagesContainerClicked);

            var heading = util.findElementByClass(appElements.CssClasses.dialogHeader, this._messageCenterDialog);
            heading.text(resources.dynamicsApp.messageCenterHeading());

            var noMessagesText = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.noMessagesText, this._messageCenterDialog);
            noMessagesText.text(resources.dynamicsApp.messageCenterNoMessages());

            this._messageLearnMore.text(resources.dynamicsApp.messageCenterLearnMoreLabel());
            this._messageLearnMore.attr(util.AriaAttributes.Label, resources.dynamicsApp.messageCenterLearnMoreLabel());
            var errorsOnlyLabel = util.findElementById(messageCenter.MessageCenterHandler.ControlIDs.errorsOnlyLabel, this._messageCenterDialog);
            errorsOnlyLabel.text(resources.dynamicsApp.messageCenterErrorsOnlyLabel());
            
            this._errorsOnlyCheckbox.change(this._updateShowErrorsOnly);
            this._clearButton.click(this.clearNotifications);
            this._clearButton.text(resources.dynamicsApp.messageCenterClearButtonLabel());
            util.changeAttribute(this._clearButton, "title", resources.dynamicsApp.messageCenterClearButtonTitle());
            this._doneButton.click(this.closeDialog);
            this._doneButton.text(resources.dynamicsApp.messageCenterDoneButtonLabel());
            util.changeAttribute(this._doneButton, "title", resources.dynamicsApp.messageCenterDoneButtonTitle());

            this.clearNotifications();
        }

        /**
         * Adds a notification to the message center.
         * @param notification The notification to add.
         * @param options The notification options.
         */
        public addNotification(notification: notifications.Notification, options?: notifications.AddNotificationOptions): void {
            options = options || {};
            var notificationElement: JQuery;
            var hasLink = options.LinkOptions !== undefined;
            const notificationCount = this._messagesContainer.children().length;
            const notificationId = messageCenter.MessageCenterHandler.createUniqueNotificationId(notificationCount);

            let timestampLabel = "";
            if (options.IncludeTimeStamp === false) {
                notificationElement = $(
                    $format(
                        messageCenter.MessageCenterHandler._notificationItemNoTimeHtmlFormat,
                        hasLink));
            } else {
                const formattedTime = messageCenter.MessageCenterHandler.getFormattedTime();
                const formattedDate = messageCenter.MessageCenterHandler.getFormattedDate();
                notificationElement = $(
                    $format(
                        messageCenter.MessageCenterHandler._notificationItemHtmlFormat,
                        formattedTime,
                        formattedDate,
                        hasLink));
                timestampLabel = resources.dynamicsApp.messageCenterNotificationTimePortionLabelFormat(formattedTime, formattedDate);
            }

            util.changeAttribute(notificationElement, MessageCenterHandler.Attributes.notificationType, notification.NotificationType);
            util.changeAttribute(notificationElement, MessageCenterHandler.Attributes.role, "listitem");
            util.changeAttribute(notificationElement, MessageCenterHandler.Attributes.id, notificationId);
            util.changeAttribute(
                notificationElement,
                util.AriaAttributes.Label,
                resources.dynamicsApp.messageCenterNotificationLabelFormat(
                    notification.NotificationType === notifications.NotificationType.error ? resources.dynamicsApp.messageCenterErrorNotificationLabel() : resources.dynamicsApp.messageCenterInformationalNotificationLabel(),
                    notification.getFormattedMessage(),
                    timestampLabel,
                    hasLink ? resources.dynamicsApp.messageCenterNotificationHasLinkPortionLabelFormat(options.LinkOptions.Text) : ""
                ));
            notificationElement.find("p").text(notification.getFormattedMessage());

            if (hasLink) {
                notificationElement
                    .keydown((event) => {
                        switch (event.which) {
                            case $.ui.keyCode.SPACE:
                            case $.ui.keyCode.ENTER:
                                options.LinkOptions.Action();
                                break;
                        }
                    })
                    .find("p")
                    .append("<br/>")
                    .append($("<a></a>", {
                        href: "#",
                        text: options.LinkOptions.Text,
                        class: "notificationLink",
                        tabindex: -1,
                        on: {
                            click: options.LinkOptions.Action
                        }
                    }));
            }
            if (notificationCount > 0) {
                var separatorClass = hasLink ? "messageCenterSeparator" : undefined;
                notificationElement.find("p").append($("<hr/>", { class: separatorClass }));
            }
            this._messagesContainer.prepend(notificationElement);
            util.changeAttribute(this._messageCenterDialog, messageCenter.MessageCenterHandler.Attributes.hasMessages, true);
        }

        /** Clears all notifications from the message center. */
        public clearNotifications(): void {
            util.changeAttribute(this._messageCenterDialog, messageCenter.MessageCenterHandler.Attributes.hasMessages, false);
            this._messagesContainer.empty();
            operationHandling.setStatus(resources.dynamicsApp.messageCenterMessagesClearedMessage());
        }

        /** Opens the message center dialog. */
        public openDialog(): void {
            this.selectShownNotificationElement();
            dialogs.handler.openDialog(messageCenter.MessageCenterHandler.ControlIDs.messageCenterDialog);
        }

        /**
         * Sets the help and support link in the message handler.
         * @param url The help and support link that will be opened when user clicks the message.
         * @param message The message shown to the user for help and support.
         */
        public setLearnMoreLink(url: string): void {
            util.changeAttribute(this._messageCenterDialog, messageCenter.MessageCenterHandler.Attributes.showLearnMoreLink, true);
            util.changeAttribute(this._messageLearnMore, "href", url);
        }

        /** Closes the message center dialog. */
        public closeDialog(): void {
            dialogs.handler.closeActiveDialog(messageCenter.MessageCenterHandler.ControlIDs.messageCenterDialog);
        }

        /** Checks the "Errors only" checkbox so that only errors are shown. */
        public showErrorsOnly(): void {
            this._errorsOnlyCheckbox.prop("checked", true);
            this._updateShowErrorsOnly();
        }

        /** Called when the "Errors only" checkbox is changed. */
        public _updateShowErrorsOnly(): void {
            var checked = this._errorsOnlyCheckbox.prop('checked');
            if (checked !== this._showErrorsOnly) {
                this._showErrorsOnly = checked;
                util.changeAttribute(this._messagesContainer, messageCenter.MessageCenterHandler.Attributes.errorsOnly, this._showErrorsOnly);
                this.selectShownNotificationElement({ setFocus: false });
            }
        }

        /**
         * Called when the messages container is clicked, attemping to select subsequently clicked list item.
         * @param event The click event.
         */
        public _messagesContainerClicked(event: JQuery.ClickEvent<HTMLElement>): void {
            if (event.target !== this._messagesContainer[0]) {
                const $target = $(event.target);
                this.selectElement($target.is(MessageCenterHandler.Selectors.notificationItem) ? $target : $target.closest(MessageCenterHandler.Selectors.notificationItem), true);
            }
        }

        /**
         * Called when a key is pressed down on the messages container, handling keyboard events to change selection and avoiding
         * undesired scrolling of the messages container.
         * @param event The key down event.
         */
        public _messagesContainerKeyDown(event: JQuery.KeyDownEvent<HTMLElement>): void {

            // Get the currently selected item
            const selectedItem = this.getSelectedNotificationElement();

            // Indicates if the event was handled
            let eventHandled = false;

            switch (event.which) {
                case $.ui.keyCode.UP: // Select first element (unless the first element is already selected)
                    this._handleArrowKeys(
                        selectedItem,
                        -1,
                        MessageCenterHandler._keyUpNextSelectionDetermination,
                        -1);
                    eventHandled = true;
                    break;
                case $.ui.keyCode.DOWN: // Select next element (unless the last element is already selected)
                    this._handleArrowKeys(
                        selectedItem,
                        0,
                        MessageCenterHandler._keyDownNextSelectionDetermination,
                        1);
                    eventHandled = true;
                    break;
                case $.ui.keyCode.HOME: // Select first element
                    this.selectShownNotificationElement();
                    eventHandled = true;
                    break;
                case $.ui.keyCode.END: // Select last element
                    this.selectShownNotificationElement({ indexToSelect: -1 });
                    eventHandled = true;
                    break;
                case $.ui.keyCode.PAGE_UP: // Prevent page up, page down, space, and enter keys from performing scrolling logic within the messages container
                case $.ui.keyCode.PAGE_DOWN:
                case $.ui.keyCode.SPACE:
                case $.ui.keyCode.ENTER:
                    eventHandled = true;
                    break;
            }

            // If the event was handled, prevent it from bubbling further
            if (eventHandled) {
                event.stopPropagation();
                event.preventDefault();
            }
        }

        /*
         * Handles determining whether selection should be moved to the previous item when the UP key is pressed.
         * @param selectedIndex The currently selected index.
         * @param lastIndex The index of the last displayed item.
         * @returns A value indicating whether selection should be moved to the previous item.
         */
        private static _keyUpNextSelectionDetermination(selectedIndex: number, lastIndex: number): boolean {
            return selectedIndex > 0;
        }

        /**
         * Handles determining whether selection should be moved to the next item when the DOWN key is pressed.
         * @param selectedIndex The currently selected index.
         * @param lastIndex The index of the last displayed item.
         * @returns A value indicating whether selection should be moved to the next item.
         */
        private static _keyDownNextSelectionDetermination(selectedIndex: number, lastIndex: number): boolean {
            return selectedIndex < lastIndex;
        }

        /**
         * Creates a unique ID for a notification element, based on the provided parameters.
         * @param notificationCount The current count of notifications. This is used to add uniqueness in the case of multiple notifications added at (nearly) the same time.
         * @returns A unique ID for a notification element.
         */
        private static createUniqueNotificationId(notificationCount: number): string {
            return `notification_${notificationCount}_${new Date().getTime()}`;
        }

        /**
         * Get the current appropriately formatted time string.
         * @returns The current appropriately formatted time string.
         */
        private static getFormattedTime(): string {
            return Globalize.format(new Date(), Globalize.culture().calendar.patterns.T);
        }

        /**
         * Gets the current appropriately formatted date string.
         * @returns The current appropriately formatted date string.
         */
        private static getFormattedDate(): string {
            return Globalize.format(new Date(), Globalize.culture().calendar.patterns.d);
        }

        /**
         * Handles adjusting selection for the UP and DOWN arrow keys based on the provided properties.
         * @param selectedItem The currently selected item.
         * @param defaultIndexToSelect The index to select by default if the currently selected item doesn't exist or can't be found (e.g. first selection).
         * @param nextSelectionDeterminationCallback The callback used to determine if selection should be moved to the next or previous (as appropriate) item.
         * @param nextSelectionIndexAdjustment The value used to adjust the next or previous index. A value of 1 will move the selection to the next item,
                                               while a value of -1 will move selection to the previous item.
         */
        private _handleArrowKeys(
            selectedItem: JQuery,
            defaultIndexToSelect: number,
            nextSelectionDeterminationCallback: (selectedIndex: number, lastIndex: number) => boolean,
            nextSelectionIndexAdjustment: number): void {

            if (util.doesElementExist(selectedItem)) {
                const listItems = this.getShownNotificationElements();
                let selectedIndex = -1;
                for (let index = 0; index < listItems.length; index++) {
                    const listItem = listItems[index];
                    if (listItem.is(MessageCenterHandler.Selectors.selectedNotificationItem)) {
                        selectedIndex = index;
                        break;
                    }
                }
                if (selectedIndex === -1) {
                    this.selectShownNotificationElement({ indexToSelect: defaultIndexToSelect });
                } else if (nextSelectionDeterminationCallback(selectedIndex, listItems.length - 1)) {
                    this.selectShownNotificationElement({ indexToSelect: selectedIndex + nextSelectionIndexAdjustment });
                }
            } else {
                this.selectShownNotificationElement({ indexToSelect: defaultIndexToSelect });
            }
        }

        /**
         * Selects a shown notification element based on the provided options. If no index is provided, the first element is
         * selected. Alternatively, if an index of -1 is provided, the last element is selected.
         * @param options The options controlling how the notification element selection is handled.
         */
        private selectShownNotificationElement(options: MessageCenterselectShownNotificationElementOptions = {}): void {

            // Get the index to select, defaulting the first item if not provided
            let indexToSelect = options.indexToSelect || 0;

            // Get the shown notification elements
            const listItems = this.getShownNotificationElements();

            // The item to select
            let itemToSelect: JQuery;

            // Determine which element should be selected if there is at least one element to select
            if (listItems.length > 0) {

                // An index of -1 indicates the last element should be selected
                if (indexToSelect === -1) {
                    indexToSelect = listItems.length - 1;
                }

                // As long as the index is within bounds, set the item to select appropriately
                if (indexToSelect >= 0 && indexToSelect < listItems.length) {
                    itemToSelect = listItems[indexToSelect];
                }
            }
            this.selectElement(itemToSelect, options.setFocus !== false);
        }

        /**
         * Select the provided element if it is defined and exists.
         * @param element The element to select.
         * @param setFocus Indicates whether the element should receive focus after selecting.
         */
        private selectElement(element: JQuery, setFocus: boolean): void {

            // Get the currently selected list item
            const selectedListItem = this.getSelectedNotificationElement();

            // If the selected list item exists, unselect it
            if (util.doesElementExist(selectedListItem)) {
                util.changeAttribute(selectedListItem, util.AriaAttributes.Selected);
                util.changeAttribute(selectedListItem, MessageCenterHandler.Attributes.tabIndex);
            }

            // The ID of the list item that was selected
            let selectedListItemId: string;

            // If the provided element to select is defined and exists, select it
            if (util.doesElementExist(element)) {

                util.changeAttribute(element, util.AriaAttributes.Selected, true);
                selectedListItemId = element.attr(MessageCenterHandler.Attributes.id);
                util.changeAttribute(element, MessageCenterHandler.Attributes.tabIndex, 0);

                // Set focus to the element if it should receive focus
                if (setFocus) {
                    util.setFocus(element);
                }
            }

            // Adjust the messages container ARIA active descendant to the currently selected item; if the provided element doesn't exists
            // the ID will be undefined and result in the attribute correctly being removed from the container.
            util.changeAttribute(this._messagesContainer, util.AriaAttributes.ActiveDescendant, selectedListItemId);
        }

        /**
         * Gets the currently selected notification element.
         * @returns The currently selected notification element.
         */
        private getSelectedNotificationElement(): JQuery {
            return this._messagesContainer.find(MessageCenterHandler.Selectors.selectedNotificationItem);
        }

        /**
         * Gets the currently shown notification elements.
         * @returns The array of currently shown notification elements.
         */
        private getShownNotificationElements(): JQuery[] {
            const shownListItemsArray = [];
            let shownListItems: JQuery;
            if (this._showErrorsOnly) {
                shownListItems = this._messagesContainer.find(MessageCenterHandler.Selectors.errorNotificationItem);
            } else {
                shownListItems = this._messagesContainer.find(MessageCenterHandler.Selectors.notificationItem);
            }
            if (util.doesElementExist(shownListItems)) {
                for (let index = 0; index < shownListItems.length; index++) {
                    shownListItemsArray.push($(shownListItems[index]));
                }
            }
            return shownListItemsArray;
        }
    }
}
